<template>
  <div
    data-testid="shop-booking-item"
    class="mb-3 flex min-h-20 flex-col items-start justify-start rounded-lg border border-main p-4 text-left tablet:flex-row tablet:items-center tablet:justify-between"
  >
    <div class="flex flex-col justify-center">
      <h2 v-if="kiosk.__typename == 'Booth'" class="txt-body-l-bold">{{ kiosk?.boothNumber || kiosk?.name }}</h2>
      <h2 v-else class="txt-body-l-bold">{{ kiosk?.name }}</h2>
      <div v-if="kiosk?.extras?.length" class="mt-1 flex flex-col gap-2 tablet:flex-row tablet:gap-6">
        <span>{{ formatPrice(totalExtrasAmount, locale as LocaleEnum) }}</span>
        <span v-if="kiosk?.extras?.length">
          {{ t('FestivalShop.StartPage.lastUpdated', { date: formatExtrasOrderDate(lastChange) }) }}
        </span>
        <span v-if="!!kiosk?.extras?.length">{{ t('FestivalShop.StartPage.items', { count: kiosk?.extras?.length ?? 0 }) }}</span>
      </div>
    </div>
    <RouterLink
      v-if="props.event?.slug && kiosk && !isPast"
      data-testid="shop-booking-select"
      class="btn-secondary-color-s my-auto"
      :to="{ name: 'shopBookingOverview', params: { eventSlug: props.event.slug, kioskId: urlifiedId } }"
    >
      {{ t('FestivalShop.StartPage.select') }}
    </RouterLink>
    <RouterLink
      v-if="props.event?.slug && kiosk && isPast && kiosk.__typename == 'Booth' && kiosk.extras?.length"
      data-testid="shop-booking-download"
      class="btn-secondary-color-s"
      :to="{ name: 'boothPrint', params: { boothId: toPlainId(props.kiosk.id) } }"
      target="_blank"
    >
      {{ t('FestivalShop.StartPage.downloadSummary') }}
    </RouterLink>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LocaleEnum, type EventsQuery, type FestivalShopKiosksQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { urlifyGlobalId } from '@/helpers/urlifyGlobalId'

import { formatPrice } from './helpers'

type Kiosk = FestivalShopKiosksQuery['festivalShopKiosks']['nodes'][number]

const props = defineProps<{
  event: NonNullable<EventsQuery['events']['nodes']>[number]
  kiosk: Kiosk
  isPast: boolean
}>()

const urlifiedId = computed(() => urlifyGlobalId(props.kiosk?.id))
const { t, locale } = useI18n()

const lastChange = computed(() => {
  return props.kiosk.extrasOrders
    ?.map((extraOrder) => extraOrder.updatedAt)
    .sort()
    .at(0)
})

const totalExtrasAmount = computed(() => {
  if (props.kiosk?.__typename !== 'Booth' || !props.kiosk.extras?.length) return 0
  return props.kiosk.extras.reduce((sum, extra) => sum + (extra?.price ?? 0) * (extra?.count ?? 0) + (extra?.deliveryFee ?? 0), 0)
})

const formatExtrasOrderDate = (value?: string | null | undefined) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
</script>
